import { SagaIterator } from 'redux-saga';
import i18next from 'i18next';
import { all, put, call, delay, takeEvery, PutEffect, select } from 'redux-saga/effects';
import get from 'lodash/get';
import { Url, URLS, sanitizeUrl } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import {
  PapiRequestDaSharedDeviationAttachmentsRemove,
  PapiRequestDaSharedDeviationAttachmentsAdd,
} from '@wix/da-papi-types';
import {
  getDeviationUrl,
  getDeviationType,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { requestNapi, requestPuppy, createFormData, isApiError } from '@wix/da-http-client';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { editDeviationSuccess } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';
import { pushModal, popModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { WriterPopupType } from '../../modals/WriterPopup/redux/types';
import {
  publishWriterDeviation,
  saveWriterDeviation,
  saveWriterDeviationSuccess,
  saveWriterDeviationFailure,
  scheduleDraftPublishSuccess,
} from '../actions';
import {
  convertResponseErrorToWriterError,
  fakeResponse,
  getWriterEndpoint,
} from '../../modals/WriterPopup/redux/utils';
import { PureBiData, SubmitContentBiEvent } from '@wix/da-bi/pkg/events';

const logger = ddt.createLogger('writer.saga');

function* dispatchSaveWriterDeviationFailure(response) {
  yield put(saveWriterDeviationFailure(convertResponseErrorToWriterError(response?.errorDetails)));
}

function* doCreateWriterDeviation(type, groupId, localState) {
  const { journalTitle: title = '', editorState, coverImage } = localState;
  logger.log('doCreateWriterDeviation', { type, groupId, localState });

  const editorRaw = JSON.stringify(editorState?.editorRaw || {});

  const cover_attachment = yield select(flagEnabled, 'cover_attachment');
  const coverImageParams = cover_attachment
    ? {}
    : {
        cover_image_deviation_id: coverImage?.deviationId,
      };
  return yield call(
    requestNapi,
    {
      method: 'post',
      url: getWriterEndpoint('/create', type),
      data: {
        title,
        group_id: groupId,
        editorRaw,
        ...coverImageParams,
      },
    },
    fakeResponse
  );
}

function* doUpdateWriterDeviation(type, groupId, deviation, localState) {
  const { journalTitle: title = '', editorState, coverImage } = localState;
  logger.log('doUpdateWriterDeviation', {
    type,
    groupId,
    deviation,
    localState,
  });
  const editorRaw = JSON.stringify(editorState?.editorRaw || {});

  const cover_attachment = yield select(flagEnabled, 'cover_attachment');
  const coverImageParams = cover_attachment
    ? {}
    : {
        cover_image_deviation_id: coverImage?.deviationId,
        reset_cover_image_deviation_id: typeof coverImage === 'undefined',
      };
  const result = yield call(
    requestNapi,
    {
      method: 'post',
      url: getWriterEndpoint('/update/content', type),
      data: {
        title,
        group_id: groupId,
        deviationid: deviation?.deviationId,
        editorRaw,
        ...coverImageParams,
      },
    },
    fakeResponse
  );

  if (!result?.deviation) {
    logger.log('failure', result);
    return result;
  }
  if (type === WriterPopupType.LITERATURE) {
    return result;
  }
  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: getWriterEndpoint('/update/metadata', type),
      data: {
        deviationid: deviation?.deviationId,
        group_id: groupId,
        featured: localState.isFeaturedPost,
        tiers:
          localState.visibility?.type === 'tier'
            ? localState.visibility.tiers.map(tier => tier.deviationId)
            : undefined,
      },
    },
    fakeResponse
  );
}

/** Sends a request to our create or update endpoint depending on if the provided deviation exists */
function* doCreateOrUpdateDeviation(type, groupId, deviation, localState) {
  let result: any = null;
  if (deviation?.deviationId === undefined) {
    result = yield call(doCreateWriterDeviation, type, groupId, localState);
  } else {
    result = yield call(doUpdateWriterDeviation, type, groupId, deviation, localState);
  }
  const updatedDeviation = result?.deviation;
  if (!updatedDeviation) {
    yield call(dispatchSaveWriterDeviationFailure, result);
    return result;
  }

  // if they've updated the cover or removed it, fire a separate xhr to the attachment endpoint
  const cover_attachment = yield select(flagEnabled, 'cover_attachment');
  const { uploadedCoverImageFile, coverImageHasChanged } = localState;
  if (cover_attachment && coverImageHasChanged) {
    if (uploadedCoverImageFile) {
      yield call(addUploadedCoverImage, { deviation: updatedDeviation, uploadedCoverImageFile });
    } else {
      yield call(removeCoverImage, { deviation: updatedDeviation });
    }
  }

  yield call(
    handleSuccess,
    { deviation: result?.deviation, type, groupId },
    deviation?.deviationId !== undefined
  );
  return result;
}

function* addUploadedCoverImage({ deviation, uploadedCoverImageFile }) {
  const data = createFormData<PapiRequestDaSharedDeviationAttachmentsAdd>({
    deviationid: deviation.deviationId,
    attachment_file: uploadedCoverImageFile,
    type: 'cover_image',
  });
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'deviation/attachments/add',
      data,
    },
    undefined
  );

  if (!result || isApiError(result)) {
    logger.log('addUploadedCoverImage error:', result);
    yield putErrorNotification(i18next.t('writer.addUploadedCoverImage.error'));
    return false;
  }

  return result.deviation;
}

function* removeCoverImage({ deviation }) {
  const fileId = deviation.coverImageFileid;
  if (!fileId) {
    yield putErrorNotification(i18next.t('writer.removeCoverImage.error'));
    return false;
  }

  const data: PapiRequestDaSharedDeviationAttachmentsRemove = {
    fileid: fileId,
    deviationid: deviation.deviationId,
    type: 'cover_image',
  };
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'deviation/attachments/remove',
      data,
    },
    undefined
  );

  if (!result || isApiError(result)) {
    logger.log('removeCoverImage error:', result);
    // todo:
    yield putErrorNotification(i18next.t('writer.removeCoverImage.error'));
    return false;
  }

  return result.deviation;
}

function* handleSuccess({ deviation, type, groupId }, wasEdit = false) {
  const { result: deviationId, entities } = normalize(deviation, deviationSchema);

  const resultActions: PutEffect<any>[] = [
    put(putEntities({ entities })),
    // Even though deviationId is expected to be a number and we're passing through the normalized
    // deviation id (which could be number | string), it should only ever receive
    // a deviation that production an id of type number
    put(saveWriterDeviationSuccess({ deviationId, type })),
  ];

  if (wasEdit) {
    resultActions.push(put(editDeviationSuccess({ deviation })));
  }

  yield all(resultActions);
  logger.log('success', deviationId);
}

function* handleRedirect(url: string) {
  logger.log('redirect', url);
  window.location.assign(sanitizeUrl(url));
}

function* handleSaveWriterDeviation(action: ReturnType<typeof saveWriterDeviation>): SagaIterator {
  const { type, deviation, groupId, doSchedule, localState } = action.payload;
  const result = yield call(doCreateOrUpdateDeviation, type, groupId, deviation, localState);
  const updatedDeviation = result?.deviation;
  if (!updatedDeviation) {
    return;
  }

  if (doSchedule) {
    yield put(
      pushModal(ModalType.DEVIATION_SUBMIT_SCHEDULE, {
        params: {
          deviation: updatedDeviation,
          scheduleTimestamp: updatedDeviation?.scheduledPublishTime,
        },
      })
    );
  } else {
    let redirectTo = getDeviationUrl(result.deviation);
    if (type === WriterPopupType.LITERATURE) {
      // Saving a Literature draft should redirect to the sta.sh draft folder
      // but since we do not know the drafts folder hash (yet to be implemented
      // on the backend) we should redirect to the sta.sh root
      redirectTo = Url.buildFromPath(URLS.STASH);

      // If its published we need to redirect back to submit not stash
      if (deviation?.isPublished) {
        redirectTo = Url.submissionLinkWithId(deviation.deviationId);
      }
    }
    yield call(handleRedirect, redirectTo);
  }
}

function* handlePublishWriterDeviation(
  action: ReturnType<typeof publishWriterDeviation>
): SagaIterator {
  const { type, groupId, refreshAfterSubmit, deviation, localState } = action.payload;
  logger.log('publishWriterDeviation', action.payload);

  const saveResult = yield call(doCreateOrUpdateDeviation, type, groupId, deviation, localState);

  if (!saveResult || !saveResult.deviation) {
    if (saveResult && saveResult.errorCode === 302) {
      window.location.replace(sanitizeUrl(saveResult.errorDetails.redirectUrl));
    }
    yield call(dispatchSaveWriterDeviationFailure, saveResult);
    return;
  }
  const deviationid = get(saveResult.deviation, 'deviationId', null);

  if (type === WriterPopupType.LITERATURE) {
    // Literature submission should finalize on the submit page
    if (!deviation?.isPublished) {
      yield call(
        logBiEvent,
        PureBiData<SubmitContentBiEvent>({
          evid: 11,
          group_exclusive: groupId !== undefined,
          typeid: ResourceType.DEVIATION,
          itemid: deviationid,
        })
      );
    }
    yield call(handleRedirect, `${Url.submissionLinkWithId(deviationid)}`);
    return;
  }
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: getWriterEndpoint('/publish', type),
      data: {
        deviationid,
        featured: localState.isFeaturedPost,
        group_id: groupId,
        tiers:
          localState.visibility?.type === 'tier'
            ? localState.visibility.tiers.map(tier => tier.deviationId)
            : undefined,
      },
    },
    fakeResponse
  );
  logger.log('response', result);

  if (result && result.deviation) {
    const returnedDeviation = result.deviation;

    yield call(
      logBiEvent,
      PureBiData<SubmitContentBiEvent>({
        evid: 11,
        group_exclusive: groupId !== undefined,
        typeid: ResourceType.JOURNAL,
        itemid: returnedDeviation.deviationId,
      })
    );

    yield call(handleSuccess, { deviation: returnedDeviation, type, groupId }, false);

    if (groupId) {
      const duration = 5000;
      yield put(
        addNotification(NotificationType.Generic, {
          message: i18next.t('writer.error.pendingApproval'),
          isError: false,
          cardProps: {
            multiLine: true,
            duration,
          },
        })
      );
      yield delay(duration);
    }

    // Upon successful submission, we should redirect
    if (refreshAfterSubmit) {
      window.location.reload();
    } else {
      yield call(handleRedirect, getDeviationUrl(returnedDeviation));
    }
  } else {
    yield call(dispatchSaveWriterDeviationFailure, result);
  }
}

function* handleSaveWriterDeviationFailure(action: ReturnType<typeof saveWriterDeviationFailure>) {
  logger.log('failure', action.payload.error);
}

function* handleScheduleSuccess(action: ReturnType<typeof scheduleDraftPublishSuccess>) {
  const { deviation } = action.payload;
  if (getDeviationType(deviation) === DeviationTypes.JOURNAL) {
    yield put(popModal());
  }
}

export default function* saga(): any {
  yield all([
    takeEvery(saveWriterDeviation, handleSaveWriterDeviation),
    takeEvery(publishWriterDeviation, handlePublishWriterDeviation),
    takeEvery(saveWriterDeviationFailure, handleSaveWriterDeviationFailure),
    takeEvery(scheduleDraftPublishSuccess, handleScheduleSuccess),
  ]);
}
